import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import ApprovalQueueCard from './ApprovalQueueCard';
import { CHECKLIST_STATUS_STYLES } from './constants';
import type { ApprovalQueueItem, ApprovalReportSummary, ChecklistCategoryId } from './types';

interface Props {
  expandedQueueItems: Record<string, boolean>;
  items: Array<ApprovalQueueItem>;
  needsApprovalCount: number;
  onApprovalChange: (rowId: string) => void;
  onSelectTab: (categoryId: ChecklistCategoryId) => void;
  onToggleDetails: (itemId: string) => void;
  reportSummaries: Array<ApprovalReportSummary>;
}

export default function ApprovalQueue({
  expandedQueueItems,
  items,
  needsApprovalCount,
  onApprovalChange,
  onSelectTab,
  onToggleDetails,
  reportSummaries,
}: Props) {
  const hasOutstandingApprovals = needsApprovalCount > 0;
  const approvalItemCopy = `checklist item${needsApprovalCount === 1 ? '' : 's'}`;
  const reportRowsCount = reportSummaries.reduce((total, summary) => total + summary.count, 0);
  const reportRowCopy = `report row${reportRowsCount === 1 ? '' : 's'}`;

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          {hasOutstandingApprovals ? <WarningAmberIcon color="warning" /> : <CheckIcon color="success" />}
          <Typography variant="body2">
            {hasOutstandingApprovals ? (
              <>
                <strong>Require approval:</strong> {needsApprovalCount} {approvalItemCopy} and {reportRowsCount}{' '}
                {reportRowCopy} still need approval.
              </>
            ) : (
              <strong>Nothing requires approval.</strong>
            )}
          </Typography>
        </Stack>
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          {reportSummaries.length === 0 ? (
            <Paper variant="outlined" sx={{ color: 'text.secondary', p: 3, textAlign: 'center' }}>
              Nothing requires approval.
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                },
              }}
            >
              {reportSummaries.map((summary) => (
                <Paper
                  key={summary.categoryId}
                  variant="outlined"
                  sx={{
                    display: 'grid',
                    gridTemplateRows: 'auto auto minmax(0, 1fr) auto',
                    height: 154,
                    p: 1.5,
                    ...CHECKLIST_STATUS_STYLES.approvalOverviewCard,
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    {summary.categoryLabel}
                  </Typography>
                  <Typography variant="h2" color="warning.dark" fontWeight={700} sx={{ my: 0.75 }}>
                    {summary.count}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                    }}
                  >
                    {summary.description}
                  </Typography>
                  <Button
                    color="warning"
                    size="small"
                    sx={{ alignSelf: 'end', justifySelf: 'start', px: 0 }}
                    endIcon={<OpenInNewIcon />}
                    onClick={() => onSelectTab(summary.categoryId)}
                  >
                    Open tab
                  </Button>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      <Stack spacing={1.5}>
        {items.length === 0 ? (
          <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
            {hasOutstandingApprovals ? 'No approval items match this view.' : 'Nothing requires approval.'}
          </Paper>
        ) : (
          items.map((queueItem) => (
            <ApprovalQueueCard
              key={queueItem.id}
              expanded={!!expandedQueueItems[queueItem.id]}
              queueItem={queueItem}
              onApprovalChange={onApprovalChange}
              onToggleDetails={onToggleDetails}
            />
          ))
        )}
      </Stack>
    </Stack>
  );
}
